import type { Axios } from 'axios';
import type FormData from 'form-data';

import { useErrorMessage } from '@/composables/useErrorMessage';
import type { ImageInterface } from '@/interfaces/base/image/index.interface';
import type { PaginatedResponse } from '@/interfaces/base/pagination/paginatedResponse.interface';
import type { ResponseApiInterface } from '@/interfaces/base/responseApi.interface';
import type { RequestParametersInterface } from '@/interfaces/user/requestParameters.interface';
import type { LoginResponseInterface } from '@/interfaces/user/responses/loginResponseInterface';
import type { UserAuthorizedResponse } from '@/interfaces/user/responses/userAuthorizedResponse.interface';
import type { UserListInterface } from '@/interfaces/user/userList.interface';
import type { LoginFormSchema } from '@/schemas/user/loginFormSchema';
import type { CreateUserPayload } from '@/schemas/user/userSchema';
import { UseHttp } from '@/utils/useHttp';
import { useRequestHandler } from '@/utils/useRequestHandler';

export class UserServices {
  private http = new UseHttp(import.meta.env.VITE_APP_URL_API, true);
  private service: Axios;
  private response: ResponseApiInterface;

  constructor() {
    this.service = this.http.getInstance;
    this.response = {
      data: null,
    };
  }

  /**
   * Realiza o login do usuário
   * @param {string} login.email
   * @param {string} login.password
   * @returns
   */
  async login(payload: LoginFormSchema) {
    try {
      const { data } = await this.service.post<LoginResponseInterface>(
        '/api/v1/auth/login',
        {
          email: payload.email.toLowerCase().trim(),
          password: payload.password,
        },
        {
          headers: { 'content-type': 'application/x-www-form-urlencoded' },
        },
      );
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      console.error(error);
      this.response = useErrorMessage(error, 'Erro ao realizar login');
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API para atualizar os dados de autenticação
   * @param {string} refreshToken token fornecido no login
   * @returns
   */
  async refreshToken(refreshToken: string) {
    try {
      const { data } = await this.service.post<{
        expiresIn: number;
        authorization: string;
        refreshToken: string;
      }>('/api/v1/auth/refreshToken', {
        refreshToken,
      });
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Erro ao atualizar dados de autenticação',
      );
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API para aceitar os termos de serviço e política de privacidade
   * @param payload
   * @returns
   */
  async acceptPolicies(payload: {
    acceptTermsOfUse: boolean;
    acceptPrivacyPolicy: boolean;
  }) {
    try {
      const { data } = await this.service.post<string>(
        '/api/v1/auth/acceptPolicies',
        payload,
      );
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Falha ao atualizar termos de uso e política de privacidade',
      );
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API para receber o email de recuperação de senha.
   * @param email
   * @returns
   */
  async recoverPassword(email: string) {
    try {
      const { data } = await this.service.post<{
        title: string;
        message: string;
      }>('/api/v1/auth/forgot-password', {
        email,
      });
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Erro enviar email de recuperação de senha',
      );
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API para redefinir a senha de usuário
   * @param {sting} token token recebido por email
   * @param {string} password senha digitada no input
   * @param {string} passwordConfirmation confirmação de senha do input
   * @returns
   */
  async newPassword(
    token: string,
    password: string,
    passwordConfirmation: string,
  ) {
    try {
      const { data } = await this.service.post<string>(
        '/api/v1/auth/change-password',
        {
          token,
          password,
          passwordConfirmation,
        },
      );
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(error, 'Erro ao redefinir a senha');
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API que retorna uma lista de usuários do sistema
   * @param params Filtros
   * @returns Lista de usuários paginada
   */
  async getUsersWithParameters(params?: Partial<RequestParametersInterface>) {
    try {
      const { data } = await this.service.get<
        PaginatedResponse<UserListInterface>
      >('/api/v1/user', {
        params,
      });
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Erro ao obter informações do usuário',
      );
      return this.response;
    }
  }

  /**
   * Filtro de busca que irá procurar pelo:
   *
   * - Nome da pessoa associada ao usuário.
   *
   * - Documento da pessoa associada ao usuário.
   *
   * - Contatos da pessoa associada ao usuário.
   * @param search
   */
  async getUsersWithAutoComplete(search?: string) {
    try {
      const { data } = await this.service.get<
        Array<{
          id: string;
          title: string;
        }>
      >('/api/v1/user/autocomplete', {
        params: {
          search,
        },
      });
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Erro ao obter informações do usuário',
      );
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API que adiciona um novo usuário ao sistema
   * @param user Dados do usuário
   * @returns
   */
  async createUser(user: CreateUserPayload) {
    try {
      const { data } = await this.service.post<{
        id: string;
        email: string;
        roleId: string;
      }>('/api/v1/user', user);
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Erro ao adicionar o usuário ao sistema',
      );
      return this.response;
    }
  }

  /**
   * Remove um usuário do sistema
   * @param id userID
   * @returns 204
   */
  async removeUser(id: string) {
    try {
      const { data } = await this.service.delete<string>(`/api/v1/user/${id}`);
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(error, 'Erro ao excluir usuário');
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API para atualizar a foto de perfil usuário
   * @param payload
   * @returns
   */
  async updateProfilePicture(payload: FormData) {
    try {
      const { data } = await this.service.post<NonNullable<ImageInterface>>(
        '/api/v1/user/upload',
        payload,
      );
      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(
        error,
        'Erro ao atualizar foto do usuário',
      );
      return this.response;
    }
  }

  /**
   * Altera a Afiliado atual do usuário
   * @param {string} token bearer token
   * @param {string} subsidiaryId id do Afiliado alvo
   * @returns status code 200
   */
  async changeSubsidiary(subsidiaryId: string) {
    try {
      const { status } = await this.service.patch<string>(
        '/api/v1/user/subsidiary/change',
        {
          subsidiary: {
            id: subsidiaryId,
          },
        },
      );

      this.response = {
        data: status,
      };
      return this.response as ResponseApiInterface<typeof status>;
    } catch (error) {
      this.response = useErrorMessage(error, 'Erro ao trocar de filial');
      return this.response;
    }
  }

  /**
   * Altera a função de um usuário em específico
   * @param {string} userId - Id do usuário cuja função será alterada.
   * @param {string} roleId - Id da nova função que será atribuída ao usuário.
   */
  async changeRole(userId: string, roleId: string) {
    try {
      const { data } = await this.service.post(
        `/api/v1/user/${userId}/role/${roleId}`,
      );

      this.response = { data };
      return this.response as ResponseApiInterface<typeof data>;
    } catch (error) {
      this.response = useErrorMessage(error, 'Erro ao trocar de função');
      return this.response;
    }
  }

  /**
   * Realiza a chamada à API para obter os dados do usuário
   */
  async getUserDetails() {
    const data = await useRequestHandler<
      UserAuthorizedResponse,
      undefined,
      undefined
    >(this.service, 'GET', '/api/v1/user/authorized', undefined, undefined);
    return data;
  }
}
